import React, { useState, useEffect, useRef } from "react";
import { X, Upload, Loader2, Image as ImageIcon } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { fileUploadAPI } from "../../services/api";

const OrganizationModal = ({
  isOpen,
  onClose,
  onSubmit,
  organization,
  title,
}) => {
  const { colors } = useTheme();

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    logo: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || "",
        address: organization.address || "",
        phone: organization.phone || "",
        logo: organization.logo || "",
      });
    } else {
      setFormData({
        name: "",
        address: "",
        phone: "",
        logo: "",
      });
    }
    setErrors({});
  }, [organization, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    }

    if (!formData.logo.trim()) {
      newErrors.logo = "Logo is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({
        ...prev,
        logo: "Please select an image file (PNG, JPG)",
      }));
      return;
    }

    // Validate string size (e.g. 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        logo: "Image size should be less than 5MB",
      }));
      return;
    }

    setUploading(true);
    setErrors((prev) => ({ ...prev, logo: "" }));

    try {
      // 1. Get presigned URL
      const response = await fileUploadAPI.getPresignedUrl(file.type);
      const { uploadUrl, imageUrl } = response.data.data;

      // 2. Upload to S3
      await fileUploadAPI.uploadFile(uploadUrl, file, file.type);

      // 3. Set URL
      setFormData((prev) => ({ ...prev, logo: imageUrl }));
    } catch (error) {
      console.error("File upload error:", error);
      setErrors((prev) => ({
        ...prev,
        logo: "Failed to upload image. Please try again.",
      }));
    } finally {
      setUploading(false);
      // Reset input so same file can be selected again if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div
        className={`${colors.cardBg} rounded-xl shadow-2xl border ${colors.cardBorder} w-full max-w-md mx-4 max-h-[90vh] transition-all transform duration-200 flex flex-col overflow-hidden`}
      >
        {/* Header */}
        <div
          className="modal-header"
        >
          <h2 className={`text-lg font-semibold ${colors.textPrimary}`}>
            {title}
          </h2>
          <button
            onClick={onClose}
            className={`p-1 ${colors.bgHover} rounded transition-colors`}
          >
            <X className={`w-5 h-5 ${colors.textMuted}`} />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Name */}
            <div>
              <label
                htmlFor="name"
                className="form-label"
              >
                Organization Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg} ${colors.inputText} ${colors.inputPlaceholder} focus:outline-none focus:ring-2 ${colors.inputFocus} focus:border-transparent ${
                  errors.name ? "border-red-300" : colors.inputBorder
                }`}
                placeholder="Enter organization name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Address */}
            <div>
              <label
                htmlFor="address"
                className="form-label"
              >
                Address *
              </label>
              <textarea
                id="address"
                name="address"
                rows="3"
                value={formData.address}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg} ${colors.inputText} ${colors.inputPlaceholder} focus:outline-none focus:ring-2 ${colors.inputFocus} focus:border-transparent ${
                  errors.address ? "border-red-300" : colors.inputBorder
                }`}
                placeholder="Enter organization address"
              />
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">{errors.address}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label
                htmlFor="phone"
                className="form-label"
              >
                Phone *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg ${colors.inputBg} ${colors.inputText} ${colors.inputPlaceholder} focus:outline-none focus:ring-2 ${colors.inputFocus} focus:border-transparent ${
                  errors.phone ? "border-red-300" : colors.inputBorder
                }`}
                placeholder="Enter phone number"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>

            {/* Logo Upload */}
            <div>
              <label
                className="form-label"
              >
                Logo *
              </label>

              {/* Hidden File Input - Always rendered to ensure ref works */}
              <input
                id="file-upload"
                name="file-upload"
                type="file"
                className="sr-only"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileChange}
              />

              <div
                className={`mt-1 flex items-center justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition-colors ${
                  errors.logo
                    ? "border-red-300"
                    : `${colors.borderSecondary} hover:border-blue-500`
                }`}
              >
                <div className="space-y-1 text-center">
                  {uploading ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
                      <p className={`mt-2 text-sm ${colors.textSecondary}`}>
                        Yükleniyor...
                      </p>
                    </div>
                  ) : formData.logo ? (
                    <div className="relative group">
                      <img
                        src={formData.logo}
                        alt="Logo preview"
                        className="mx-auto h-24 w-24 object-contain rounded-lg shadow-sm bg-white p-1"
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-40 rounded-lg">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-white text-sm font-medium hover:underline"
                        >
                          Değiştir
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload
                        className={`mx-auto h-12 w-12 ${colors.textTertiary}`}
                      />
                      <div className={`flex text-sm ${colors.textSecondary}`}>
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                        >
                          <span>Dosya Yükle</span>
                        </label>
                        <p className="pl-1">veya sürükleyip bırakın</p>
                      </div>
                      <p className={`text-xs ${colors.textTertiary}`}>
                        PNG, JPG, GIF (Max 5MB)
                      </p>
                    </>
                  )}
                </div>
              </div>
              {errors.logo && (
                <p className="mt-1 text-sm text-red-600">{errors.logo}</p>
              )}
            </div>
          </div>

          {/* Buttons - Fixed Footer */}
          <div
            className={`flex space-x-3 p-6 border-t ${colors.border} shrink-0`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 border ${colors.btnSecondaryBorder} ${colors.btnSecondaryText} ${colors.btnSecondaryBg} rounded-lg ${colors.btnSecondaryHover} transition-colors`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {loading ? "Saving..." : organization ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganizationModal;
